import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/chalkApi";
import { useAuth } from "./AuthContext";

const TeamCtx = createContext(null);
const STORAGE_KEY = "chalkos.activeTeam";

export function TeamProvider({ children }) {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState(localStorage.getItem(STORAGE_KEY) || null);
  const [tier, setTier] = useState({ coach_tier: "pickup", guardian_tier: "pickup" });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await api.listTeams();
      const list = data.teams || [];
      setTeams(list);
      // Pick a sensible active team: stored if still present, else first active
      let next = activeTeamId;
      if (!next || !list.find((t) => t.id === next)) {
        const firstActive = list.find((t) => t.status === "active") || list[0];
        next = firstActive ? firstActive.id : null;
      }
      setActiveTeamId(next);
      if (next) localStorage.setItem(STORAGE_KEY, next);
    } finally {
      setLoading(false);
    }
  }, [user, activeTeamId]);

  useEffect(() => {
    if (user) refresh();
    else {
      setTeams([]);
      setActiveTeamId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.id]);

  // Resolve tier whenever active team changes
  const loadTier = useCallback(async () => {
    if (!activeTeamId) {
      setTier({ coach_tier: "pickup", guardian_tier: "pickup" });
      return;
    }
    try {
      const data = await api.tierFor(activeTeamId);
      setTier({ coach_tier: data.coach_tier, guardian_tier: data.guardian_tier || "pickup" });
    } catch {
      // ignore
    }
  }, [activeTeamId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadTier();
    })();
    return () => { cancelled = true; };
  }, [loadTier]);

  const setActive = useCallback((id) => {
    setActiveTeamId(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const activeTeam = useMemo(
    () => teams.find((t) => t.id === activeTeamId) || null,
    [teams, activeTeamId]
  );

  const value = useMemo(
    () => ({ teams, activeTeam, activeTeamId, setActive, refresh, refreshTier: loadTier, loading, tier }),
    [teams, activeTeam, activeTeamId, setActive, refresh, loadTier, loading, tier]
  );

  return (
    <TeamCtx.Provider value={value}>
      {children}
    </TeamCtx.Provider>
  );
}

export function useTeam() {
  const v = useContext(TeamCtx);
  if (!v) throw new Error("useTeam must be inside TeamProvider");
  return v;
}
